import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Menu, X, ChevronDown, Gauge } from "lucide-react";
import { Logo } from "./Logo";
import { SERVICES } from "../data/services";
import { BIZ, track } from "../data/site";

const PANEL_W = 544; // w-[34rem]

const linkCls = ({ isActive }) =>
  `whitespace-nowrap font-mono text-[10px] 2xl:text-[11px] tracking-[0.18em] uppercase transition-colors duration-300 ${
    isActive ? "text-accent" : "text-mute hover:text-accent"
  }`;

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [svcOpen, setSvcOpen] = useState(false);
  const [svcFlip, setSvcFlip] = useState(false);
  const svcRef = useRef(null);
  const svcBtnRef = useRef(null);

  useEffect(() => {
    setOpen(false);
    setServicesOpen(false);
    setSvcOpen(false);
    if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
  }, [pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close the desktop Services panel on outside click/tap
  useEffect(() => {
    if (!svcOpen) return;
    const onDown = (e) => {
      if (svcRef.current && !svcRef.current.contains(e.target)) setSvcOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
    };
  }, [svcOpen]);

  const openSvc = () => {
    // If a 544px panel anchored to the button's left edge would overflow the
    // viewport on the right, anchor it to the button's right edge instead.
    if (svcBtnRef.current) {
      const r = svcBtnRef.current.getBoundingClientRect();
      setSvcFlip(r.left + PANEL_W > window.innerWidth - 16);
    }
    setSvcOpen(true);
  };

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-ink/90 backdrop-blur-xl border-b border-white/[0.07] py-3" : "bg-transparent py-4"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between gap-4">
        <Link to="/" aria-label="Westway Collision Center — home" className="shrink-0">
          <Logo variant="header" shrunk={scrolled} />
        </Link>

        {/* Full desktop nav — 1280px and wider only */}
        <nav className="hidden xl:flex items-center gap-5 2xl:gap-7" aria-label="Primary">
          <div
            ref={svcRef}
            className="relative"
            onMouseEnter={openSvc}
            onMouseLeave={() => setSvcOpen(false)}
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                setSvcOpen(false);
                svcBtnRef.current?.focus();
              }
            }}
            onBlur={(e) => {
              if (!e.currentTarget.contains(e.relatedTarget)) setSvcOpen(false);
            }}
          >
            <button
              ref={svcBtnRef}
              type="button"
              data-testid="nav-services-dropdown"
              onClick={() => (svcOpen ? setSvcOpen(false) : openSvc())}
              className={`inline-flex items-center gap-1.5 whitespace-nowrap font-mono text-[10px] 2xl:text-[11px] tracking-[0.18em] uppercase transition-colors duration-300 py-2 hover:text-accent ${
                svcOpen || pathname.startsWith("/services") ? "text-accent" : "text-mute"
              }`}
              aria-haspopup="true"
              aria-expanded={svcOpen}
              aria-controls="services-menu"
            >
              Services <ChevronDown size={12} className={`transition-transform duration-300 ${svcOpen ? "rotate-180" : ""}`} />
            </button>
            <div
              className={`absolute top-full pt-3 z-50 max-w-[calc(100vw-2rem)] transition-all duration-300 ${
                svcFlip ? "right-0" : "left-0"
              } ${svcOpen ? "visible opacity-100" : "invisible opacity-0"}`}
            >
              <div
                id="services-menu"
                className="w-[34rem] max-w-[calc(100vw-2rem)] max-h-[calc(100vh-6rem)] overflow-y-auto rounded-2xl border border-white/10 bg-ink/95 backdrop-blur-xl p-5 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.8)]"
              >
                <div className="grid grid-cols-1 min-[560px]:grid-cols-2 gap-1">
                  {SERVICES.map((s) => {
                    const active = pathname === `/services/${s.slug}`;
                    return (
                      <Link
                        key={s.slug}
                        to={`/services/${s.slug}`}
                        data-testid={`nav-service-${s.slug}`}
                        aria-current={active ? "page" : undefined}
                        onClick={() => setSvcOpen(false)}
                        className={`rounded-lg px-3 py-2.5 text-sm whitespace-nowrap transition-colors hover:bg-white/[0.05] flex items-center justify-between gap-2 ${
                          active ? "bg-accent/10 text-accent font-semibold" : "text-slate-300 hover:text-white"
                        }`}
                      >
                        {s.menuName}
                        {s.featured && (
                          <span className="rounded-full border border-accent/40 px-2 py-0.5 font-mono text-[8px] tracking-[0.14em] uppercase text-accent/80">
                            Our Specialty
                          </span>
                        )}
                      </Link>
                    );
                  })}
                </div>
                <Link
                  to="/services"
                  data-testid="nav-all-services"
                  onClick={() => setSvcOpen(false)}
                  className="mt-3 flex items-center justify-center rounded-xl border border-accent/30 bg-accent/[0.06] px-3 py-2.5 font-mono text-[10px] tracking-[0.2em] uppercase text-accent hover:bg-accenthover hover:text-white transition-colors"
                >
                  View All Services
                </Link>
              </div>
            </div>
          </div>

          <NavLink
            to="/dme-tuning-dallas"
            data-testid="nav-dme-tuning"
            className={({ isActive }) =>
              `inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-3.5 py-1.5 font-mono text-[10px] 2xl:text-[11px] tracking-[0.14em] uppercase transition-colors duration-300 ${
                isActive
                  ? "border-accentdeep bg-accentdeep text-white"
                  : "border-accent/40 bg-accent/10 text-accent hover:bg-accenthover hover:text-white"
              }`
            }
          >
            <Gauge size={12} />
            DME Tuning
          </NavLink>
          <NavLink to="/free-estimate" data-testid="nav-link-estimate" className={linkCls}>Free Estimate</NavLink>
          <NavLink to="/our-work" data-testid="nav-link-our-work" className={linkCls}>Our Work</NavLink>
          <NavLink to="/services/insurance-claims" data-testid="nav-link-insurance" className={linkCls}>Insurance</NavLink>
          <NavLink to="/services/financing" data-testid="nav-link-financing" className={linkCls}>Financing</NavLink>
          <NavLink to="/about" data-testid="nav-link-about" className={linkCls}>About</NavLink>
          <NavLink to="/contact" data-testid="nav-link-contact" className={linkCls}>Contact</NavLink>
          <a
            href={BIZ.phoneHref}
            data-testid="nav-call-cta"
            onClick={() => track("click_to_call", { location: "navbar" })}
            className="inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-accent/40 bg-accent/10 px-4 py-2.5 font-mono text-[11px] tracking-[0.14em] text-accent hover:bg-accenthover hover:text-white transition-colors duration-300"
          >
            <Phone size={13} strokeWidth={2.2} />
            {BIZ.phoneDisplay}
          </a>
        </nav>

        {/* Below 1280px: phone + Free Estimate stay visible, hamburger handles the rest */}
        <div className="flex xl:hidden items-center gap-2 sm:gap-3">
          <a
            href={BIZ.phoneHref}
            data-testid="nav-call-cta-compact"
            onClick={() => track("click_to_call", { location: "navbar" })}
            aria-label={`Call ${BIZ.phoneDisplay}`}
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full border border-accent/40 bg-accent/10 h-11 w-11 sm:w-auto sm:px-4 font-mono text-[11px] tracking-[0.14em] text-accent hover:bg-accenthover hover:text-white transition-colors duration-300"
          >
            <Phone size={14} strokeWidth={2.2} />
            <span className="hidden sm:inline" aria-hidden="true">{BIZ.phoneDisplay}</span>
          </a>
          <Link
            to="/free-estimate"
            data-testid="nav-estimate-cta-compact"
            className="inline-flex items-center justify-center whitespace-nowrap rounded-full bg-accentdeep px-4 min-h-11 font-display font-bold text-xs sm:text-sm text-white transition-all duration-300 hover:bg-accenthover"
          >
            <span className="hidden min-[420px]:inline">Free Estimate</span>
            <span className="min-[420px]:hidden">Estimate</span>
          </Link>
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen(!open)}
            className="text-white p-2.5 -mr-2"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            aria-controls="mobile-menu"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            id="mobile-menu"
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="xl:hidden overflow-hidden bg-ink/95 backdrop-blur-xl border-b border-white/[0.07] max-h-[80vh] overflow-y-auto"
            aria-label="Mobile"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              <button
                data-testid="nav-mobile-services-toggle"
                onClick={() => setServicesOpen(!servicesOpen)}
                className="flex items-center justify-between min-h-11 font-display text-xl font-semibold text-slate-100"
                aria-expanded={servicesOpen}
                aria-controls="mobile-services-list"
              >
                Services
                <ChevronDown size={18} className={`text-accent transition-transform duration-300 ${servicesOpen ? "rotate-180" : ""}`} />
              </button>
              <AnimatePresence>
                {servicesOpen && (
                  <motion.div
                    id="mobile-services-list"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden"
                  >
                    <div className="flex flex-col gap-1 pl-4 pb-2 border-l border-accent/25">
                      <Link to="/services" onClick={() => setOpen(false)} className="inline-flex items-center min-h-11 text-sm font-semibold text-accent">All Services</Link>
                      {SERVICES.map((s) => {
                        const active = pathname === `/services/${s.slug}`;
                        return (
                          <Link
                            key={s.slug}
                            to={`/services/${s.slug}`}
                            onClick={() => setOpen(false)}
                            aria-current={active ? "page" : undefined}
                            className={`text-sm transition-colors inline-flex items-center gap-2 min-h-11 ${active ? "text-accent font-semibold" : "text-slate-300 hover:text-accent"}`}
                          >
                            {s.menuName}
                            {s.featured && <span className="rounded-full border border-accent/40 px-1.5 py-px font-mono text-[8px] tracking-[0.12em] uppercase text-accent/80">Specialty</span>}
                          </Link>
                        );
                      })}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
              {[
                ["Performance & DME Tuning", "/dme-tuning-dallas", true],
                ["Free Estimate", "/free-estimate"],
                ["Our Work", "/our-work"],
                ["Insurance Claims", "/services/insurance-claims"],
                ["Financing", "/services/financing"],
                ["About", "/about"],
                ["Contact", "/contact"],
              ].map(([label, to, accent]) => (
                <Link
                  key={to}
                  to={to}
                  data-testid={`nav-mobile-${to.replace(/\//g, "-")}`}
                  onClick={() => setOpen(false)}
                  aria-current={pathname === to ? "page" : undefined}
                  className={`font-display text-xl font-semibold transition-colors inline-flex items-center gap-2.5 min-h-11 ${accent ? "text-white" : pathname === to ? "text-accent" : "text-slate-100 hover:text-accent"}`}
                >
                  {accent ? <span className="h-2 w-2 rotate-45 bg-accent shrink-0" aria-hidden="true" /> : null}{label}
                </Link>
              ))}
              <a
                href={BIZ.phoneHref}
                data-testid="nav-mobile-call-cta"
                onClick={() => track("click_to_call", { location: "mobile_menu" })}
                className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-accentdeep px-6 py-4 font-semibold text-white"
              >
                <Phone size={16} />
                Call {BIZ.phoneDisplay}
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
